import LeadBannerTrackingService from './analytics/LeadBannerTrackingService';
import {
    LeadBannerPlacement,
    LeadBannerTriggerReason,
} from './analytics/types';
import generateCardId from './generateCardId';

export interface ContextInput {
    contextKey: string;
    placement: LeadBannerPlacement;
    position: number | null;
    triggerReason: LeadBannerTriggerReason;
}

interface ContextState {
    cardId: string;
    sent: {
        impression: boolean;
        sheetOpen: boolean;
        formStart: boolean;
        formSubmit: boolean;
    };
}

export interface UseLeadBannerTrackingReturn {
    onImpression(input: ContextInput): void;
    onSheetOpen(input: ContextInput): void;
    onFormStart(input: ContextInput): void;
    onFormSubmit(input: ContextInput): void;
    resetContexts(): void;
    getCardId(contextKey: string): string | undefined;
}

function createEmptyContextState(): ContextState {
    return {
        cardId: generateCardId(),
        sent: {
            impression: false,
            sheetOpen: false,
            formStart: false,
            formSubmit: false,
        },
    };
}

export default function useLeadBannerTracking(): UseLeadBannerTrackingReturn {
    const service = LeadBannerTrackingService.getInstance();
    const contexts = new Map<string, ContextState>();

    function onImpression(input: ContextInput): void {
        const existing = contexts.get(input.contextKey);

        if (!existing) {
            const cardId = generateCardId();
            contexts.set(input.contextKey, {
                cardId,
                sent: {
                    impression: true,
                    sheetOpen: false,
                    formStart: false,
                    formSubmit: false,
                },
            });
            service.impression({
                placement: input.placement,
                card_id: cardId,
                position: input.position,
                trigger_reason: input.triggerReason,
            });
            return;
        }

        if (existing.sent.impression) {
            return;
        }

        // Защитный fallback: контекст уже есть, но impression ещё не помечен.
        // Теоретически невозможно (флаг ставится при создании), но защищает от рассинхрона.
        existing.sent.impression = true;
        service.impression({
            placement: input.placement,
            card_id: existing.cardId,
            position: input.position,
            trigger_reason: input.triggerReason,
        });
    }

    function onSheetOpen(input: ContextInput): void {
        let state = contexts.get(input.contextKey);

        if (!state) {
            state = createEmptyContextState();
            contexts.set(input.contextKey, state);
        }

        if (state.sent.sheetOpen) {
            return;
        }

        state.sent.sheetOpen = true;
        service.sheetOpen({
            placement: input.placement,
            card_id: state.cardId,
            position: input.position,
            trigger_reason: input.triggerReason,
        });
    }

    function onFormStart(input: ContextInput): void {
        let state = contexts.get(input.contextKey);

        if (!state) {
            state = createEmptyContextState();
            contexts.set(input.contextKey, state);
        }

        if (state.sent.formStart) {
            return;
        }

        state.sent.formStart = true;
        service.formStart({
            placement: input.placement,
            card_id: state.cardId,
            trigger_reason: input.triggerReason,
        });
    }

    function onFormSubmit(input: ContextInput): void {
        let state = contexts.get(input.contextKey);

        if (!state) {
            state = createEmptyContextState();
            contexts.set(input.contextKey, state);
        }

        if (state.sent.formSubmit) {
            return;
        }

        state.sent.formSubmit = true;
        service.formSubmit({
            placement: input.placement,
            card_id: state.cardId,
            trigger_reason: input.triggerReason,
        });
    }

    function resetContexts(): void {
        contexts.clear();
    }

    function getCardId(contextKey: string): string | undefined {
        return contexts.get(contextKey)?.cardId;
    }

    return {
        onImpression,
        onSheetOpen,
        onFormStart,
        onFormSubmit,
        resetContexts,
        getCardId,
    };
}
